import { PaymentData } from '../../types';

// IBAN patterns by country with exact lengths
const IBAN_PATTERNS = {
  BE: { pattern: /^BE[0-9]{2}[0-9]{12}$/, length: 16 }, // Belgian IBAN
  DE: { pattern: /^DE[0-9]{2}[0-9]{18}$/, length: 22 }, // German IBAN
  FR: { pattern: /^FR[0-9]{2}[0-9]{20}$/, length: 24 }, // French IBAN
};

interface ValidationResult {
  isValid: boolean;
  errors: {
    name?: string;
    iban?: string;
    amount?: string;
    reference?: string;
  };
}

export function validatePaymentData(data: PaymentData): ValidationResult {
  const errors: ValidationResult['errors'] = {};
  
  // Validate name
  if (!data.name?.trim()) {
    errors.name = 'Recipient name is required';
  } else if (data.name.length > 70) {
    errors.name = 'Name must be less than 70 characters';
  }

  // Validate IBAN
  if (!data.iban) {
    errors.iban = 'IBAN is required';
  } else {
    const cleanIban = data.iban.replace(/\s+/g, '').toUpperCase();
    const countryCode = cleanIban.slice(0, 2);
    const countryPattern = IBAN_PATTERNS[countryCode as keyof typeof IBAN_PATTERNS];

    if (cleanIban.length < 15) {
      errors.iban = 'IBAN is too short';
    } else if (countryPattern && !countryPattern.pattern.test(cleanIban)) {
      errors.iban = `Invalid ${countryCode} IBAN format`;
    } else if (!countryPattern) {
      errors.iban = 'Only BE, DE, and FR IBANs are supported';
    }
  }

  // Validate amount
  if (!data.amount || data.amount <= 0) {
    errors.amount = 'Amount must be greater than 0';
  } else if (data.amount > 999999999.99) {
    errors.amount = 'Amount is too large';
  }

  // Validate structured reference if present
  if (data.reference?.startsWith('+++')) {
    const digits = data.reference.replace(/[^0-9]/g, '');
    if (digits.length !== 12) {
      errors.reference = 'Structured reference must be exactly 12 digits';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}